/**
 * data/quests/side — 10 optional side quests given by tavern regulars
 * (side-quests.md). Each names an ARCHETYPE giver (`archetype:<playStyle>`) that
 * the session binds at load to the first available `lod:'regular'` of that
 * playStyle homed near the relevant node (falling back to any regular). Givers
 * speak in their own personality via the voice layer; the hook/completion lines
 * here are the authored floor.
 *
 * Every quest: auto-tracked objectives, a no-money reward, a warm completion beat.
 * None expire, none can fail, all are ignorable.
 *
 * NOTE (frozen-contract deviation): shared/quest.ts allows ONE `reward` per quest;
 * several side quests in the doc grant two. We encode the SIGNATURE reward and note
 * the doc's secondary reward in an `INTENDED EXTRAS` comment for the session's grant
 * path (or a future `reward: QuestReward[]`). The single-reward picks are chosen to
 * cover every reward KIND across the set. See the return summary.
 */
import type { ObjectiveDef, QuestDef, QuestStage, StageDialogue } from '@/shared';

function stage(
  id: string,
  objectives: readonly ObjectiveDef[],
  onStart: readonly string[],
  onComplete: readonly string[],
): QuestStage {
  const dialogue: StageDialogue = { onStart, onComplete };
  return { id, objectives, dialogue };
}

/** A one-stage side quest (all 10 are single-stage). */
function sideQuest(
  id: string,
  title: string,
  giver: string,
  synopsis: string,
  objectives: readonly ObjectiveDef[],
  hook: readonly string[],
  completion: readonly string[],
  reward: QuestDef['reward'],
  tone: readonly string[],
): QuestDef {
  return {
    id,
    kind: 'side',
    title,
    giver,
    synopsis,
    available: { kind: 'always' },
    tone,
    reward,
    stages: [stage('stage-1', objectives, hook, completion)],
  };
}

export const SIDE_QUESTS: readonly QuestDef[] = [
  // 1 — INTENDED EXTRAS: + rare-candies 2
  sideQuest(
    'side-breeder-eggs',
    'A Nursery of Little Ones',
    'archetype:breeder',
    'The nursery-keeper is wonderful at hellos — help welcome a few new faces.',
    [{ kind: 'evolve', count: 2 }],
    [
      "Oh, hello! I'm rubbish at goodbyes but wonderful at hellos — and nothing says hello like a fresh face. Would you help me welcome a few?",
    ],
    [
      "Look at them! You've got the gentlest hands — my whole nursery's smitten. Come back any time you fancy a cuddle.",
    ],
    { kind: 'gift-mon', speciesId: 'togepi', level: 5 },
    ['warm', 'giddy'],
  ),

  // 2 — INTENDED EXTRAS: + codex-unlock the-beetle-ledger
  sideQuest(
    'side-collector-bugcatch',
    'The Beetle Ledger',
    'archetype:collector',
    'A collector’s bug pages have gone gappy — fill a few in for them.',
    [
      { kind: 'visit-biome', biomeId: 'whispering-forest' },
      { kind: 'catch-type', monType: 'bug', count: 3 },
    ],
    [
      'I keep a ledger of every little critter in the wood, and my bug pages have gone all gappy. Fancy filling a few in for me?',
    ],
    [
      "Three new pages, and every one a beauty! The ledger thanks you, and so do I. You've a collector's eye, you know.",
    ],
    { kind: 'evolution-stone', itemId: 'leaf-stone' },
    ['warm', 'meticulous'],
  ),

  // 3 — INTENDED EXTRAS: + codex-unlock region-history
  sideQuest(
    'side-explorer-grandtour',
    'The Grand Tour',
    'archetype:explorer',
    'An explorer’s theory: the whole region is one long walk. Prove it — no hurry.',
    [
      { kind: 'visit-biome', biomeId: 'still-lake' },
      { kind: 'visit-biome', biomeId: 'quiet-cavern' },
      { kind: 'visit-biome', biomeId: 'mountain-slope' },
    ],
    [
      "I've a theory the whole region's just one long walk in disguise. Care to prove it with me? No hurry — the good bits don't go anywhere.",
    ],
    [
      "Three corners of the world in one stretch of days — and you smiled at every one. That's the whole secret, right there. Onward, whenever you like.",
    ],
    { kind: 'town-prop', propId: 'tour-map' },
    ['warm', 'wandering'],
  ),

  // 4 — INTENDED EXTRAS: + codex-unlock the-lantern-keeper
  sideQuest(
    'side-battler-spar',
    'A Friendly Measure',
    'archetype:battler',
    'A battler loves a good clean spar — trade a few no-stakes rounds.',
    [{ kind: 'win-friendly-battles', count: 3 }],
    [
      "No stakes, no grudges — I just love a good clean spar, and yours look like they'd give a grand one. Trade a few friendly rounds with me?",
    ],
    [
      "Three rounds and not one hard word between us — that's how it's meant to be done. You spar like a friend, not a rival. I'll remember that.",
    ],
    { kind: 'rare-candies', count: 4 },
    ['warm', 'sporting'],
  ),

  // 5 — INTENDED EXTRAS: + town-prop applause-banner
  sideQuest(
    'side-socializer-showoff',
    'Peacock Evening',
    'archetype:socializer',
    'It’s a bragging night — give the tavern something to gasp at.',
    [{ kind: 'show-off', count: 3 }],
    [
      "Tonight's a bragging night! Everyone's showing off their best, and I told them you'd steal the show. Go on — give the tavern something to gasp at!",
    ],
    [
      "Did you HEAR them?! Three gasps and a whole round of 'ooohs' — you're the toast of the tavern. Take a bow, you've earned it.",
    ],
    { kind: 'follower-cosmetic', cosmeticId: 'show-ribbon' },
    ['warm', 'theatrical'],
  ),

  // 6 — INTENDED EXTRAS: + codex-unlock the-old-shrine
  sideQuest(
    'side-collector-fossil',
    'The Stone That Remembers',
    'archetype:collector',
    'A collector longs to meet an old, old friend locked in the ruins’ rock.',
    [
      { kind: 'visit-biome', biomeId: 'old-ruins' },
      { kind: 'catch-species', speciesId: 'kabuto' },
    ],
    [
      "They say the ruins keep old, old friends locked in the rock. I'd give anything to meet one — would you find me a relic that still stirs?",
    ],
    [
      "It blinked at me — actually blinked! A friend older than the town itself, and you brought us together. I'll never stop marveling.",
    ],
    { kind: 'gift-mon', speciesId: 'omanyte', level: 18 },
    ['warm', 'wondering'],
  ),

  // 7 — INTENDED EXTRAS: + rare-candies 3
  sideQuest(
    'side-breeder-stone',
    'A Change of Heart',
    'archetype:breeder',
    'A breeder wants to watch a Growlithe bloom into Arcanine, together.',
    [
      { kind: 'catch-species', speciesId: 'growlithe' },
      { kind: 'evolve', count: 1, toSpeciesId: 'arcanine' },
    ],
    [
      "Some little ones only bloom with a nudge — a warm stone, a bit of sunlight. I've a Growlithe who's ready. Would you raise one alongside me so we can watch them change together?",
    ],
    [
      "There it goes — all grown and glorious! There's no feeling like it, is there? Watching something you love become exactly what it was meant to be.",
    ],
    { kind: 'evolution-stone', itemId: 'fire-stone' },
    ['warm', 'nurturing'],
  ),

  // 8 — INTENDED EXTRAS: + codex-unlock the-lantern-keeper
  sideQuest(
    'side-explorer-trade',
    'Hands Across the Region',
    'archetype:explorer',
    'Half the joy of wandering is the swaps you make — do a trade for the fellowship of it.',
    [{ kind: 'trade', count: 1 }],
    [
      "Half the joy of wandering is the folks you meet and the swaps you make. I've a friend who'd love something of yours. Would you do a trade or two, for the fellowship of it?",
    ],
    [
      "A trade's a little promise, isn't it? 'I'll think of you when I see this one.' You've made the whole region feel a touch smaller and a lot warmer today.",
    ],
    { kind: 'gift-mon', speciesId: 'haunter', level: 25 },
    ['warm', 'fellowship'],
  ),

  // 9 — INTENDED EXTRAS: + bump the newcomer's friendshipWithPlayer (new greetable friend)
  sideQuest(
    'side-socializer-newcomer',
    'Say Hello for Me',
    'archetype:socializer',
    'A shy new face has been hovering by the door — go say hello.',
    [{ kind: 'talk-to', trainerId: 'archetype:newcomer' }],
    [
      "There's a shy new face who's been hovering by the door for days. I'm hopeless — I'd only fluster them. But you? You've a kind way. Would you go say hello?",
    ],
    [
      "You SPOKE to them and now they won't stop smiling! See? I knew you'd know what to say. That's two friends you've made today — them, and me all over again.",
    ],
    { kind: 'town-prop', propId: 'welcome-wreath' },
    ['warm', 'shy'],
  ),

  // 10 — INTENDED EXTRAS: + rare-candies 2
  sideQuest(
    'side-battler-castshow',
    'Light Up the Rafters',
    'archetype:battler',
    'The tavern’s gone sleepy — flourish a fiery move indoors and wake it up.',
    [{ kind: 'cast-move', count: 2, monType: 'fire' }],
    [
      "The tavern's gone all sleepy tonight. Bet you could wake it right up — flourish a move indoors, something with a bit of dazzle! Careful of the lanterns, mind.",
    ],
    [
      "HA! The whole room lit up gold and everyone cheered! That's the tavern properly awake now. Do it again some time — no reason needed, just for the joy of it.",
    ],
    { kind: 'follower-cosmetic', cosmeticId: 'ember-sparkle' },
    ['warm', 'playful'],
  ),
];
